import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import { Card } from "../components/ui/card";
import { ShieldCheck, Database, Cookie, Globe, Trash2, Mail, Baby } from "lucide-react";
import { openExternal } from "../lib/native";

/**
 * Standalone privacy policy — required by Apple App Store & Google Play as a
 * dedicated URL. Duplicates and expands on section 8 of /policies with the
 * data-processor inventory reviewers expect.
 */
export default function Privacy() {
  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-10">
        <div className="inline-flex items-center gap-2 bg-[#EBE8E0] text-[#1B3624] px-3 py-1.5 rounded-full text-xs font-semibold tracking-wide uppercase">
          <ShieldCheck className="w-3.5 h-3.5" /> Privacy Policy
        </div>
        <h1 data-testid="privacy-title" className="mt-4 text-3xl sm:text-4xl tracking-tighter font-medium" style={{ fontFamily: "Outfit" }}>
          How Binshare handles your data
        </h1>
        <p className="mt-2 text-[#6B6965]">
          Last updated February 2026. Binshare Ltd (&ldquo;we&rdquo;, &ldquo;us&rdquo;) is the data controller of the personal data you provide when using the Binshare app and marketing site. UK-GDPR and the Data Protection Act 2018 apply.
        </p>

        <Card className="mt-8 p-7 rounded-2xl border-[#E2DFD8] bg-white space-y-7">

          <Section icon={<Database className="w-5 h-5" />} title="1. What we collect and why">
            <p>We only collect what is needed to operate a UK waste-clearance marketplace:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Account info</strong> — name, email, chosen role. Lawful basis: contract performance.</li>
              <li><strong>Password (hashed)</strong> — bcrypt-hashed, never stored in plaintext. Lawful basis: contract.</li>
              <li><strong>Phone number</strong> — only if you opt into SMS notifications or the waiting-list callout. Lawful basis: consent.</li>
              <li><strong>Location</strong> — city/postcode you type when posting; precise coordinates only when you enable &ldquo;Near me&rdquo; and grant browser geolocation. Lawful basis: consent + contract.</li>
              <li><strong>Photos</strong> — waste-post images and bin-listing images you upload. Lawful basis: contract.</li>
              <li><strong>Carrier documents</strong> — Environment Agency waste carrier licence copy. Lawful basis: legal obligation + contract.</li>
              <li><strong>Payment records</strong> — transaction amounts, statuses, timestamps (card details are handled solely by Stripe — we never see or store them). Lawful basis: contract + legal obligation (HMRC records).</li>
              <li><strong>Messages</strong> — in-app chat between homeowners, businesses and carriers. Lawful basis: contract.</li>
              <li><strong>Analytics events</strong> — pages visited, feature usage. Lawful basis: consent (see cookie banner) or legitimate interest for aggregate stats.</li>
            </ul>
          </Section>

          <Section icon={<Globe className="w-5 h-5" />} title="2. Third-party processors (subprocessors)">
            <p>Binshare shares personal data with the following processors only for the specific purposes noted. Each is bound by a Data Processing Agreement and, where applicable, UK-approved international transfer safeguards.</p>
            <div className="overflow-x-auto -mx-2 sm:mx-0">
              <table className="w-full text-sm border-collapse" data-testid="subprocessors-table">
                <thead>
                  <tr className="text-left border-b border-[#E2DFD8] text-[#6B6965]">
                    <th className="py-2 pr-3 font-medium">Processor</th>
                    <th className="py-2 pr-3 font-medium">Purpose</th>
                    <th className="py-2 font-medium">Data</th>
                  </tr>
                </thead>
                <tbody className="align-top">
                  {SUBPROCESSORS.map((s) => (
                    <tr key={s.name} className="border-b border-[#F0EEE8] last:border-0">
                      <td className="py-2 pr-3">
                        <div className="font-medium text-[#1B3624]">{s.name}</div>
                        <div className="text-[11px] text-[#6B6965]">{s.location}</div>
                      </td>
                      <td className="py-2 pr-3">{s.purpose}</td>
                      <td className="py-2 text-[#4A4842]">{s.data}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>

          <Section icon={<Cookie className="w-5 h-5" />} title="3. Cookies & similar technologies">
            <p>We use two kinds of storage:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Strictly necessary</strong> — an HttpOnly session token so we know you&apos;re logged in, and localStorage for your role/theme preferences. Always on. Cannot be disabled without breaking the app.</li>
              <li><strong>Analytics</strong> — PostHog stores an anonymised event/session identifier for product analytics on the web app only. Enabled only after you accept the cookie banner. You can withdraw consent at any time by clicking <em>Cookie preferences</em> in the footer. Analytics is entirely disabled inside the iOS and Android apps — no tracking, no ATT prompt.</li>
            </ul>
            <p>We do not use advertising cookies or third-party marketing pixels.</p>
          </Section>

          <Section icon={<Trash2 className="w-5 h-5" />} title="4. How long we keep your data">
            <ul className="list-disc pl-5 space-y-1">
              <li>Transaction records — <strong>7 years</strong> (HMRC accounting law).</li>
              <li>Account profile & messages — while your account is active. Deleted within <strong>30 days</strong> of a verified deletion request.</li>
              <li>Photos & posts — deleted when you delete the post or your account.</li>
              <li>Analytics events — <strong>13 months</strong>, then anonymised.</li>
              <li>Password reset tokens — <strong>1 hour</strong>.</li>
            </ul>
          </Section>

          <Section icon={<ShieldCheck className="w-5 h-5" />} title="5. Your rights (UK-GDPR)">
            <p>You have the right to: access your data, correct it, delete it (&ldquo;right to be forgotten&rdquo;), object to processing, restrict processing, and receive a portable copy.</p>
            <p><strong>Delete your account any time from within the app.</strong> Sign in and go to <em>Menu &rarr; Settings &rarr; Delete Account</em>. Your account, open posts, Reuse listings, messages and notifications are removed immediately. Historical transaction records are kept for the other party&apos;s business history but your name is replaced with &ldquo;Deleted User&rdquo; and your email &amp; phone number are removed. You cannot delete while a job is in progress or money is in escrow &mdash; complete or cancel those first.</p>
            <p>To exercise any other right, email <a href="mailto:support@binshare.co.uk" className="text-[#4A6B56] underline">support@binshare.co.uk</a>. We respond within 30 days.</p>
            <p>If you&apos;re unhappy with our response you can complain to the UK Information Commissioner&apos;s Office at <a href="https://ico.org.uk" onClick={(e) => { e.preventDefault(); openExternal("https://ico.org.uk"); }} className="text-[#4A6B56] underline">ico.org.uk</a>.</p>
          </Section>

          <Section icon={<Baby className="w-5 h-5" />} title="6. Children">
            <p>Binshare is not directed at children under 18. We do not knowingly collect data from anyone under 18. If you believe a child has an account, email us and we&apos;ll delete it.</p>
          </Section>

          <Section icon={<Mail className="w-5 h-5" />} title="7. Contact & changes">
            <p>Data-protection contact: <a href="mailto:support@binshare.co.uk" data-testid="privacy-support-email" className="text-[#4A6B56] underline">support@binshare.co.uk</a>.</p>
            <p>We may update this policy as our service evolves. Material changes will be announced in-app and reflected here. Continued use after a change means you accept the revised policy.</p>
          </Section>

        </Card>

        <div className="mt-6 text-sm text-[#6B6965] text-center">
          Full <Link to="/policies" className="text-[#4A6B56] underline">Terms of Service</Link> · <Link to="/" className="hover:text-[#1B3624]">← Back to Binshare</Link>
        </div>
      </div>
    </Layout>
  );
}

// Kept out of the component body so the JSX stays scannable; edit here when
// you add a new processor.
const SUBPROCESSORS = [
  { name: "Stripe Payments UK Ltd", location: "UK / Ireland", purpose: "Card payments, escrow, carrier payouts (Stripe Connect Express)", data: "Name, email, transaction amount, card & bank details (Stripe-hosted; we do not see card numbers)" },
  { name: "Resend", location: "USA (SCCs)", purpose: "Transactional email (welcome, password reset, bid alerts)", data: "Name, email, message content" },
  { name: "PostHog Inc.", location: "USA / EU (SCCs)", purpose: "Product analytics", data: "Anonymised user id, pages visited, feature events" },
  { name: "OpenStreetMap Foundation (Nominatim)", location: "UK / EU", purpose: "Geocoding addresses to coordinates", data: "Address string only (no user identity)" },
  { name: "Apple Inc.", location: "USA (SCCs)", purpose: "Sign in with Apple", data: "Apple ID token, email (real or private-relay)" },
  { name: "Google LLC", location: "USA (SCCs)", purpose: "Sign in with Google", data: "Google account email, name, profile picture URL" },
  { name: "Emergent Object Storage", location: "UK / EU", purpose: "Storage of uploaded photos and licence documents", data: "Files you upload, associated URL keys" },
  { name: "Cloudflare Inc.", location: "USA (SCCs)", purpose: "DNS + edge routing for binshare.co.uk", data: "IP address (log-level only)" },
];

function Section({ icon, title, children }) {
  return (
    <div>
      <div className="flex items-center gap-2 text-[#1B3624]">
        {icon}
        <h2 className="text-xl tracking-tight font-medium" style={{ fontFamily: "Outfit" }}>{title}</h2>
      </div>
      <div className="mt-2 text-sm text-[#4A4842] leading-relaxed space-y-2">{children}</div>
    </div>
  );
}
